import { useCallback } from 'react'

type UseTableKeyboardNavigationProps<T> = {
    totalRowsOnPage: number
    focusedRowIndexRef: React.MutableRefObject<number>
    updateFocusedRow: (newIndex: number) => void
    // Selection props (optional - only needed if table has selectable rows)
    rowsOnPage?: ReadonlyArray<T>
    selections?: T[]
    setSelections?: (selections: T[]) => void
    disabledSelections?: T[]
    getId?: (row: T) => string
}

export const useTableKeyboardNavigation = <T>({
    totalRowsOnPage,
    focusedRowIndexRef,
    updateFocusedRow,
    rowsOnPage = [],
    selections,
    setSelections,
    disabledSelections,
    getId = (x): string => (x as { id: string }).id,
}: UseTableKeyboardNavigationProps<T>): {
    handleTableKeyDown: (e: React.KeyboardEvent<HTMLTableSectionElement>) => void
} => {
    const handleTableKeyDown = useCallback((e: React.KeyboardEvent<HTMLTableSectionElement>): void => {
        if (totalRowsOnPage === 0) return

        // Don't intercept keyboard events if focus is inside a menu, popover, or other overlay
        // These components have their own keyboard navigation
        const target = e.target as HTMLElement
        if (target.closest('[role="menu"], [role="listbox"], [role="dialog"], .MuiPopover-root, .MuiPopper-root, .MuiModal-root')) {
            return
        }

        const currentIndex = focusedRowIndexRef.current

        // Shift+Space: Toggle selection of current row
        if (e.shiftKey && e.key === ' ' && setSelections && rowsOnPage.length > 0) {
            e.preventDefault()
            const currentRow = rowsOnPage[currentIndex]
            if (!currentRow) return

            const rowId = getId(currentRow)
            const disabledSet = new Set(disabledSelections?.map((item) => getId(item)) ?? [])

            if (disabledSet.has(rowId)) return

            const currentSelections = selections ?? []
            const selectionIds = new Set(currentSelections.map((item) => getId(item)))

            if (selectionIds.has(rowId)) {
                setSelections(currentSelections.filter((item) => getId(item) !== rowId))
            } else {
                setSelections([...currentSelections, currentRow])
            }
            return
        }

        // Ctrl/Cmd+A: Select all rows on current page
        if ((e.ctrlKey || e.metaKey) && e.key === 'a' && setSelections && rowsOnPage.length > 0) {
            e.preventDefault()
            const disabledSet = new Set(disabledSelections?.map((item) => getId(item)) ?? [])
            const currentSelections = selections ?? []
            const existingSelectionIds = new Set(currentSelections.map((item) => getId(item)))

            const newSelections = [...currentSelections]
            for (const row of rowsOnPage) {
                const rowId = getId(row)
                if (!disabledSet.has(rowId) && !existingSelectionIds.has(rowId)) {
                    newSelections.push(row)
                }
            }
            setSelections(newSelections)
            return
        }

        // Arrow key navigation
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                updateFocusedRow(Math.min(currentIndex + 1, totalRowsOnPage - 1))
                break
            case 'ArrowUp':
                e.preventDefault()
                updateFocusedRow(Math.max(currentIndex - 1, 0))
                break
            case 'Home':
                e.preventDefault()
                updateFocusedRow(0)
                break
            case 'End':
                e.preventDefault()
                updateFocusedRow(totalRowsOnPage - 1)
                break
            default:
                break
        }
    }, [
        totalRowsOnPage,
        focusedRowIndexRef,
        updateFocusedRow,
        rowsOnPage,
        selections,
        setSelections,
        disabledSelections,
        getId,
    ])

    return { handleTableKeyDown }
}
